import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, memo, useState } from 'react';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { EmployeeResource } from '@/types/resources';
import { api } from '@/utils/api';

import EmployeeForm from './EmployeeForm';

interface Props {
  employee: EmployeeResource;
}

const EmployeeTableRow: FC<Props> = memo(({ employee }) => {
  const alerts = useAlerts();

  const [showEditForm, setShowEditForm] = useState(false);

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        'Are you sure you would like to delete this employee',
        employee.name,
      );

      if (!confirmed) return;

      await api.delete(`/v1/employees/${employee.id}`);

      queryClient.invalidateQueries(['employees'], {
        exact: false,
      });

      alerts.success('Employee successfully deleted', employee.name);
    } catch (_) {
      alerts.error();
    }
  };

  const onEdit = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowEditForm(true);
  };

  return (
    <tr>
      <Td>
        <Text as="span" $type="smallHeadline">
          {employee.name}
        </Text>
      </Td>
      <Td>{employee.roles!.map((r) => r.title).join(', ')}</Td>
      <Td>
        <ActionsDropdown>
          <ActionsDropdownButton as="button" onClick={onEdit}>
            <PencilIcon />
            Edit
          </ActionsDropdownButton>
          <AnimatePresence>
            {showEditForm && (
              <EmployeeForm
                editableEmployee={employee}
                onClose={() => setShowEditForm(false)}
              />
            )}
          </AnimatePresence>
          <ActionsDropdownButton as="button" onClick={onDelete}>
            <TrashIcon />
            Delete
          </ActionsDropdownButton>
        </ActionsDropdown>
      </Td>
    </tr>
  );
});

export default EmployeeTableRow;
